import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@context/NotificationContext';
import { Sun, Moon, Bell } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';
import MessageCard from '@components/messages/MessageCard';
import RatingInboxCard from '@components/messages/RatingInboxCard';
import JoinInboxCard from '@components/messages/JoinInboxCard';
import RatingReminderInboxCard from '@components/messages/RatingReminderInboxCard';
import ActivityReminderInboxCard from '@components/messages/ActivityReminderInboxCard';
import PhotoFollowupInboxCard from '@components/messages/PhotoFollowupInboxCard';
import ShareRequestInboxCard from '@components/messages/ShareRequestInboxCard';
import { useMessagesInbox } from '@hooks/useNotifications';

const InboxSkeleton = () => (
  <div className="flex flex-col gap-3">
    {[1, 2, 3, 4].map((i) => (
      <div
        key={i}
        className="h-[4.75rem] animate-pulse rounded-[18px] bg-surface-200/80 dark:bg-surface-800/80"
      />
    ))}
  </div>
);

const MessagesPage = () => {
  const navigate = useNavigate();
  const { dark, toggleTheme } = useTheme();
  const { refreshUnreadCount } = useNotifications();
  const {
    conversations,
    ratings,
    joins,
    ratingReminders,
    loading,
    ratingsLoading,
    markActivityRead,
    onRatingRead,
    onJoinRead,
    onRatingReminderRead,
    onActivityReminderRead,
    activityReminders,
    photoFollowups,
    onPhotoFollowupRead,
    memoryShares,
    onMemoryShareHandled,
    pulsingActivityId,
  } = useMessagesInbox();

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  const feed = useMemo(() => {
    const parts = [
      ...conversations.map((c) => ({
        kind: 'message',
        ts: new Date(c.sortAt || c.time).getTime(),
        key: `m-${c.activityId}`,
        conversation: c,
      })),
      ...ratings.map((n) => ({
        kind: 'rating',
        ts: new Date(n.createdAt).getTime(),
        key: `r-${n._id}`,
        rating: n,
      })),
      ...joins.map((n) => ({
        kind: 'join',
        ts: new Date(n.createdAt).getTime(),
        key: `j-${n._id}`,
        join: n,
      })),
      ...ratingReminders.map((n) => ({
        kind: 'rating_reminder',
        ts: new Date(n.createdAt).getTime(),
        key: `rr-${n._id}`,
        reminder: n,
      })),
      ...activityReminders.map((n) => ({
        kind: 'activity_reminder',
        ts: new Date(n.createdAt).getTime(),
        key: `ar-${n._id}`,
        activityReminder: n,
      })),
      ...photoFollowups.map((n) => ({
        kind: 'photo_followup',
        ts: new Date(n.createdAt).getTime(),
        key: `pf-${n._id}`,
        photoFollowup: n,
      })),
      ...memoryShares.map((n) => ({
        kind: 'memory_share',
        ts: new Date(n.createdAt).getTime(),
        key: `ms-${n._id}`,
        memoryShare: n,
      })),
    ];
    parts.sort((a, b) => b.ts - a.ts);
    return parts;
  }, [conversations, ratings, joins, ratingReminders, activityReminders, photoFollowups, memoryShares]);

  const listLoading = loading || ratingsLoading;

  const openConversation = async (c) => {
    await markActivityRead(c.activityId);
    navigate(`/activity/${c.activityId}#messages`);
  };

  return (
    <div className="min-h-screen bg-surface-50 pb-28 pt-safe dark:bg-surface-950 lg:pb-10">
      <header className="sticky top-0 z-40 glass-header">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3 px-4 pb-3 pt-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-surface-900 dark:text-white">
              Notifications
            </h1>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="theme-toggle shrink-0"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg scroll-smooth px-4 pb-8">
        <section className="mt-6">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300">
              <Bell size={18} strokeWidth={2} />
            </div>
            <h2 className="text-sm font-semibold tracking-wide text-surface-600 dark:text-surface-400">
              Vibe updates
            </h2>
          </div>

          {listLoading ? (
            <InboxSkeleton />
          ) : feed.length === 0 ? (
            <div className="rounded-[18px] border border-dashed border-surface-300/90 bg-white/80 px-5 py-12 text-center dark:border-surface-600/60 dark:bg-surface-900/50">
              <p className="text-sm font-medium text-surface-600 dark:text-surface-300">
                Nothing new yet
              </p>
              <p className="mt-6 text-xs text-surface-500 dark:text-surface-500">
                When there&apos;s something new on your vibes—joins, leaves, ratings, reminders, or post-vibe
                check-ins—you&apos;ll see it here.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {feed.map((item) =>
                item.kind === 'message' ? (
                  <MessageCard
                    key={item.key}
                    conversation={item.conversation}
                    isPulsing={String(pulsingActivityId) === String(item.conversation.activityId)}
                    onClick={() => openConversation(item.conversation)}
                  />
                ) : item.kind === 'rating' ? (
                  <RatingInboxCard key={item.key} n={item.rating} onRatingRead={onRatingRead} />
                ) : item.kind === 'join' ? (
                  <JoinInboxCard key={item.key} n={item.join} onJoinRead={onJoinRead} />
                ) : item.kind === 'activity_reminder' ? (
                  <ActivityReminderInboxCard
                    key={item.key}
                    n={item.activityReminder}
                    onReminderRead={onActivityReminderRead}
                  />
                ) : item.kind === 'photo_followup' ? (
                  <PhotoFollowupInboxCard
                    key={item.key}
                    n={item.photoFollowup}
                    onFollowupRead={onPhotoFollowupRead}
                  />
                ) : item.kind === 'memory_share' ? (
                  <ShareRequestInboxCard
                    key={item.key}
                    n={item.memoryShare}
                    onShareHandled={onMemoryShareHandled}
                  />
                ) : (
                  <RatingReminderInboxCard
                    key={item.key}
                    n={item.reminder}
                    onReminderRead={onRatingReminderRead}
                  />
                )
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default MessagesPage;
